import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Package, QrCode, Clock, Eye, Sparkles, ArrowUpRight } from "lucide-react";

const StatTile = ({ label, value, icon: Icon, tone = "default" }) => (
  <div className="hed-card p-4 md:p-6 min-w-0" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="hed-overline text-[10px] md:text-[11px] truncate">{label}</div>
        <div className="font-serif text-3xl md:text-4xl mt-2 md:mt-3 text-[#1A1A18]">{value}</div>
      </div>
      <div
        className={`w-8 h-8 md:w-9 md:h-9 flex-shrink-0 rounded-sm flex items-center justify-center ${
          tone === "gold"
            ? "bg-[#B5953B] text-white"
            : tone === "olive"
            ? "bg-[#5E6033] text-white"
            : "bg-[#F2F0EA] text-[#5E6033]"
        }`}
      >
        <Icon size={16} strokeWidth={1.5} />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  return (
    <AdminLayout>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8 md:mb-10">
        <div className="min-w-0">
          <div className="hed-overline">Dashboard</div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mt-2 tracking-tight">
            Good day at the studio.
          </h1>
          <p className="text-sm text-[#5C5C54] mt-2 max-w-xl">
            A glance at your catalog, QR codes and AI activity.
          </p>
        </div>
        <Link
          to="/admin/products/new"
          className="inline-flex items-center gap-2 bg-[#B5953B] hover:bg-[#9A7E32] text-white px-4 py-2.5 md:px-5 md:py-3 rounded-sm font-medium transition-colors text-sm"
          data-testid="dashboard-new-product-button"
        >
          <Plus size={16} strokeWidth={2} /> New product
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 hed-stagger">
        <StatTile label="Total Products" value={stats?.total_products ?? "—"} icon={Package} tone="gold" />
        <StatTile label="QR Codes" value={stats?.total_qrs ?? "—"} icon={QrCode} tone="olive" />
        <StatTile label="Awaiting Approval" value={stats?.pending_approval ?? "—"} icon={Clock} />
        <StatTile label="AI Extracting" value={stats?.pending_extraction ?? "—"} icon={Sparkles} />
      </div>

      {/* Most Viewed + Recent */}
      <div className="grid lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 hed-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">Most viewed</h3>
            <div className="hed-overline">By scans</div>
          </div>
          {stats?.most_viewed?.length ? (
            <div className="divide-y divide-[#E8E6DF]">
              {stats.most_viewed.map((p, idx) => (
                <Link
                  to={`/admin/products/${p.id}/edit`}
                  key={p.id}
                  className="py-3 flex items-center justify-between hover:bg-[#F2F0EA] -mx-2 px-2 rounded-sm transition-colors"
                  data-testid={`most-viewed-item-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="font-serif text-2xl text-[#B5953B] w-8">{idx + 1}</div>
                    <div>
                      <div className="font-medium">{p.name || "Untitled"}</div>
                      <div className="text-xs text-[#5C5C54]">{p.category || "—"} · {p.code}</div>
                    </div>
                  </div>
                  <div className="text-sm flex items-center gap-1 text-[#5C5C54]">
                    <Eye size={14} strokeWidth={1.5} /> {p.views || 0}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#5C5C54]">No views yet. Publish a product to start tracking.</p>
          )}
        </div>

        <div className="hed-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">Recent uploads</h3>
            <Link to="/admin/products" className="text-xs hed-overline hover:text-[#B5953B]" data-testid="view-all-products-link">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {(stats?.recent_products || []).slice(0, 5).map((p) => (
              <Link
                key={p.id}
                to={`/admin/products/${p.id}/edit`}
                className="flex items-center gap-3 hover:bg-[#F2F0EA] -mx-2 px-2 py-2 rounded-sm transition-colors"
              >
                <div className="w-10 h-10 bg-[#F2F0EA] border border-[#E8E6DF] rounded-sm flex items-center justify-center overflow-hidden">
                  {p.images?.[0] ? (
                    <img src={fileUrl(p.images[0].storage_path)} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <Package size={16} strokeWidth={1.5} className="text-[#5E6033]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name || "Untitled draft"}</div>
                  <div className="text-[11px] text-[#5C5C54]">
                    {p.status === "published" ? "Published" : "Draft"} · {p.extraction_status}
                  </div>
                </div>
                <ArrowUpRight size={14} strokeWidth={1.5} className="text-[#5C5C54]" />
              </Link>
            ))}
            {!stats?.recent_products?.length && (
              <p className="text-sm text-[#5C5C54]">No uploads yet.</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
